import { TriStateBool } from "../Core/TriStateBool";
import { ByteArrayUtils } from "./ByteArrayUtils";
import { HashSet } from "./HashSet";
import { List } from "./List";

export class StringUtils {
    public static WildcardToRegex(pattern: string): string {
        return "^" + StringUtils.EscapeRegExp(pattern).
            replace("\\*", ".*").
            replace("\\?", ".") + "$";
    }

    public static EscapeRegExp(str: string): string {
        return str.replace(/[\-\[\]\/\{\}\(\)\*\+\?\.\\\^\$\|]/g, "\\$&");
    }

    public static GetHashCode(val: string): number {
        var hash = 0, i, chr, len;
        if (val.length == 0) return hash;
        for (i = 0, len = val.length; i < len; i++) {
            chr = val.charCodeAt(i);
            hash = ((hash << 5) - hash) + chr;
            hash |= 0; // Convert to 32bit integer
        }
        return hash;
    }

    public static Format(val: string, ...args: any[]): string {
        return val.replace(/{(\d+)}/g, function (match, number) {
            return typeof args[number] != 'undefined'
                ? args[number]
                : match
                ;
        });
    }

    public static IsNullOrWhiteSpace(s: string): boolean {
        if (typeof s === 'undefined' || s == null) {
            return true;
        }
        return s.replace(/\s/g, '').length < 1;
    }

    public static IsNullOrEmpty(s: string): boolean {
        return (s === undefined || s === null || s === "");
    }

    public static StartsWith(s: string, t: string): boolean {
        return (s.indexOf(t) == 0);
    }

    public static ParseBool(s: string): boolean {
        var lowerCase = s.toLowerCase();
        return ((lowerCase === 'true') || (lowerCase === 't'));
    }

    public static ParseTriStateBool(s: string): TriStateBool {
        var n = parseInt(s);
        if (n === 1)
            return TriStateBool.True;
        else if (n === 2)
            return TriStateBool.False;
        else
            return TriStateBool.Unknown;
    }

    public static get Empty(): string {
        return "";
	}

	public static StringArrayToCsv(a: string[]): string {
		console.assert(!!a);
		let result = "";
		for (let i = 0; i < a.length; ++i) {
			console.assert(!!a[i]);
			if (i > 0)
				result += ", ";
			result += a[i];
		}
		return result;
	}

	public static CsvToStringArray(csv: string): string[] {
		console.assert(!!csv);
		if (!csv) {
			return [];
		}
		else {
			let result = csv.split(',');
			for (let i = 0; i < result.length; ++i)
			{
				if (!!result[i])
					result[i] = result[i].trim();
			}
			return result;
		}
	}

	public static SetToCsv<T>(set: HashSet<T>, valueEncoder: (value: T) => string): string {
		let stringList = new List<string>();
		set.ForEach((value: T) => {
			stringList.Add(valueEncoder(value));
		});
		return StringUtils.StringArrayToCsv(stringList.ToArray());
	}

	public static CsvToSet<T>(csv: string, set: HashSet<T>, valueDecoder: (value: string) => T): void {
		let stringArr = StringUtils.CsvToStringArray(csv);
		stringArr.forEach((str: string) => {
			let value = valueDecoder(str);
			set.Add(value);
		});
	}

	public static AppendNameValuePair(str: string, name: string, value: string): string {
		if (str) {
			str += ", ";
		}
		else {
			str = "";
		}

		str += `${name}=${value}`;

		return str;
	}

	public static CaseInsensitiveEquals(a: string, b: string): boolean {
		return (a.localeCompare(b, undefined, { sensitivity: 'accent' }) === 0);
	}

	public static Repeat(s: string, n: number): string {
		let result = "";
		for (let i = 0; i < n; ++i) {
			result += s;
		}
		return result;
	}

	public static ObfuscateString2(p: string): string {
		return StringUtils.ObfuscateString(p, 179);
	}

	public static ClarifyString2(e: string): string {
		return StringUtils.ClarifyString(e, 179);
	}

	public static ObfuscateString(s: string, k: number): string {

		if (s === undefined || s === null || this.IsObfuscatedString(s)) {
			return s;
		}
		else if (s === "") {
			return '\u0000';
		}

		let encoder = new TextEncoder();
		let p: Uint8Array = encoder.encode(s);

		let n: number = p.length;
		let e = new Uint8Array(n + 1);

		e[0] = (k ^ p[0]);

		for (let i: number = 0; i < n; ++i) {
			let next: number = (i + 1) % n;
			e[i + 1] = (p[i] ^ p[next]);
		}

		let result = '\u0000' + ByteArrayUtils.BytesToHexString(e);
		return result;
	}

	public static ClarifyString(s: string, k: number): string {
		if (s === undefined || s === null || !StringUtils.IsObfuscatedString(s))
			return s;
		else
			s = s.substr(1);

		if (!!s) {
			let e: Uint8Array = ByteArrayUtils.HexStringToBytes(s);

			let n: number = e.length - 1;
			let p = new Uint8Array(n);

			p[0] = (e[0] ^ k);

			for (let i: number = 1; i < n; ++i) {
				p[i] = (e[i] ^ p[i - 1]);
			}

			let decoder = new TextDecoder('utf-8');
			let result = decoder.decode(p);
			return result;
		}
		else {
			return s;
		}
	}

	public static IsObfuscatedString(s: string): boolean {
		return (!!s && s.length >= 1 && s[0] == '\u0000');
	}

	public static ObfuscateStrings2(strList: List<string>): void {
		for (let i = 0; i < strList.Count; ++i) {
			strList.Set(i, StringUtils.ObfuscateString2(strList.Get(i)));
		}
	}

	public static ClarifyStrings2(strList: List<string>): void {
		for (let i = 0; i < strList.Count; ++i) {
			strList.Set(i, StringUtils.ClarifyString2(strList.Get(i)));
		}
	}

	//public static ObfuscateString(p: string, k: number): string {
	//	if (!!p) {
	//		let n: number = p.length;
	//		let e: string = "";

	//		let e0: string = String.fromCodePoint(k ^ p.codePointAt(0));
	//		e += e0;

	//		for (let i: number = 0; i < n; ++i) {
	//			let otherCharIndex: number = (i + 1) % n;
	//			let ei: string = String.fromCodePoint(p.codePointAt(i) ^ p.codePointAt(otherCharIndex));
	//			e += ei;
	//		}

	//		return e;
	//	}
	//	else {
	//		return p;
	//	}
	//}

	//public static ClarifyString(e: string, k: number): string {
	//	if (!!e) {
	//		let n: number = e.length - 1;
	//		let p = "";

	//		let p0: string = String.fromCodePoint(e.codePointAt(0) ^ k);
	//		p += p0;

	//		for (let i: number = 1; i < n; ++i) {
	//			let pi: string = String.fromCodePoint(e.codePointAt(i) ^ p.codePointAt(i - 1));
	//			p += pi;
	//		}

	//		return p;
	//	}
	//	else {
	//		return e;
	//	}
	//}

	//public static fromCodePoint(...codePoints: number[]): string {
	//	var codeUnits = [], codeLen = 0, result = "";
	//	for (var index = 0, len = codePoints.length; index !== len; ++index) {
	//		var codePoint = +codePoints[index];
	//		// correctly handles all cases including `NaN`, `-Infinity`, `+Infinity`
	//		// The surrounding `!(...)` is required to correctly handle `NaN` cases
	//		// The (codePoint>>>0) === codePoint clause handles decimals and negatives
	//		if (!(codePoint < 0x10FFFF && (codePoint >>> 0) === codePoint))
	//			throw RangeError("Invalid code point: " + codePoint);
	//		if (codePoint <= 0xFFFF) { // BMP code point
	//			codeLen = codeUnits.push(codePoint);
	//		} else { // Astral code point; split in surrogate halves
	//			// https://mathiasbynens.be/notes/javascript-encoding#surrogate-formulae
	//			codePoint -= 0x10000;
	//			codeLen = codeUnits.push(
	//				(codePoint >> 10) + 0xD800,  // highSurrogate
	//				(codePoint % 0x400) + 0xDC00 // lowSurrogate
	//			);
	//		}
	//		if (codeLen >= 0x3fff) {
	//			result += String.fromCharCode.apply(null, codeUnits);
	//			codeUnits.length = 0;
	//		}
	//	}
	//	return result + String.fromCharCode.apply(null, codeUnits);
	//}

}
